import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { DeliveryShipment } from './types';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

interface DeliveriesPendingResponse {
  readonly shipments: readonly DeliveryShipment[];
}

/**
 * `GET /api/v1/merchant/deliveries/pending` — Sprint 14's own "pending
 * dispatch" operator view (IMPLEMENTATION_MASTER_PLAN.md S14 row, DEV
 * §10.1). Read-only: no mutation exists on this resource — the only real
 * recovery path is the queue's own retry/circuit-breaker/dead-letter
 * cycle, never a merchant-triggered action (see DEV §10.1: "manually
 * escalated", not "manually retried").
 */
export function useDeliveriesPendingQuery(): UseQueryResult<readonly DeliveryShipment[], ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: ['deliveries-pending', activeShopId],
    queryFn: async () => {
      const response = await apiRequest<DeliveriesPendingResponse>('/merchant/deliveries/pending', {
        accessToken,
        shopId: activeShopId,
      });
      return response.shipments;
    },
    enabled: accessToken !== null && activeShopId !== null,
  });
}
